import { useEffect, useMemo, useRef, type RefObject } from "react";
import { useSearchableContext, type Searchable, type SearchState } from "./SearchableContext";
import { createSearchRegex, findScrollContainer, scrollToCenterElement, measureTextareaOffset, type SearchResult } from "./utils";

export interface TextareaSearchableOptions {
  /** Unique identifier for this searchable */
  id?: string;
  /** Display name (e.g., "Braindump", "Synopsis") */
  name?: string;
  /** Function to get current value (for controlled components) */
  getValue?: () => string;
  /** Function to set new value (for controlled components) */
  setValue?: (value: string) => void;
}

export interface TextareaSearchableResult {
  searchable: Searchable | null;
  searchState: SearchState;
  fieldId: string | undefined;
}

/**
 * Makes a textarea searchable within the global find/replace system
 * @param textareaRef - Ref to the textarea element
 * @param options - Configuration options
 * @returns { searchable, searchState } for use in rendering highlights
 */
export function useTextareaSearchable(
  textareaRef: RefObject<HTMLTextAreaElement | null>,
  options: TextareaSearchableOptions
): TextareaSearchableResult {
  const { id, name, getValue, setValue } = options;

  const { register, unregister, searchState } = useSearchableContext();

  // Keep latest getValue/setValue in refs to avoid re-registering
  const getValueRef = useRef(getValue);
  const setValueRef = useRef(setValue);

  useEffect(() => {
    getValueRef.current = getValue;
    setValueRef.current = setValue;
  }, [getValue, setValue]);

  const searchable = useMemo<Searchable | null>(() => {
    // Don't create searchable if no id is provided
    if (!id) return null;

    return {
      id,
      name: name || id,

      search: (term: string): SearchResult[] => {
        const value = getValueRef.current?.() || textareaRef.current?.value || "";
        const regex = createSearchRegex(term, "gi");
        if (!regex || !value) return [];

        const results: SearchResult[] = [];
        let match: RegExpExecArray | null;

        // Reset regex state
        regex.lastIndex = 0;

        while ((match = regex.exec(value)) !== null) {
          if (match[0].trim()) {
            results.push({
              from: match.index,
              to: match.index + match[0].length,
              sourceId: id,
              sourceName: name || id,
              matchText: match[0],
            });
          }
        }

        return results;
      },

      replace: (result: SearchResult, replacement: string) => {
        const value = getValueRef.current?.() || textareaRef.current?.value || "";
        const newValue =
          value.substring(0, result.from) +
          replacement +
          value.substring(result.to);

        if (setValueRef.current) {
          setValueRef.current(newValue);
        } else if (textareaRef.current) {
          textareaRef.current.value = newValue;
          // Trigger change event for React controlled components
          const event = new Event("input", { bubbles: true });
          textareaRef.current.dispatchEvent(event);
        }
      },

      replaceAll: (term: string, replacement: string) => {
        const value = getValueRef.current?.() || textareaRef.current?.value || "";
        const regex = createSearchRegex(term, "gi");
        if (!regex || !value) return;

        const newValue = value.replace(regex, replacement);

        if (setValueRef.current) {
          setValueRef.current(newValue);
        } else if (textareaRef.current) {
          textareaRef.current.value = newValue;
          const event = new Event("input", { bubbles: true });
          textareaRef.current.dispatchEvent(event);
        }
      },

      scrollToResult: (result: SearchResult) => {
        const textarea = textareaRef.current;
        if (!textarea?.isConnected) return;

        // Set selection to highlight the match
        textarea.setSelectionRange(result.from, result.to);

        // Measure the actual Y position of the match by creating a temporary measurement element
        // This accounts for word wrapping, unlike just counting newlines
        const matchOffset = measureTextareaOffset(textarea, result.from);

        // Scroll the page to center the match
        const scrollContainer = findScrollContainer(textarea);
        scrollToCenterElement(textarea, matchOffset, scrollContainer);
      },

      getElement: (): HTMLElement | null => {
        return textareaRef.current;
      },
    };
  }, [id, name, textareaRef]);

  useEffect(() => {
    if (searchable) {
      register(searchable);
      return () => unregister(id!);
    }
  }, [searchable, register, unregister, id]);

  // Return both the searchable and searchState for highlight rendering
  return { searchable, searchState, fieldId: id };
}

export default useTextareaSearchable;
